import { useSearchParams } from 'react-router-dom';
import { useAuthStore, useDrawerStore } from '../../../store';
import React, { useEffect, useState } from 'react';
import { useDeleteData, useFetchData, useProductStore } from '../../../hooks';
import { PageableResponse, ProductModel } from '../../../types';
import { Resource } from '../../../lib/enums';
import { Spin } from 'antd';
import BaseTable from '../../../components/BaseTable';
import InfoModal from '../../../components/ui/modal/InfoModal';
import { convertDate, dateTimeFormat } from '../../../lib/utils';

const ProductTable = ({
  api,
  moduleName,
  moduleCode,
}: {
  api: string;
  moduleName: string;
  moduleCode: string;
}) => {
  const [searchParams] = useSearchParams();
  const drawerStore = useDrawerStore();
  const productStore = useProductStore();
  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const { user } = useAuthStore();
  const userModuleAccess = user?.moduleAccess?.find(
    (item) => item.module.code === moduleCode,
  );

  const {
    mutate,
    isPending: isPendingDelete,
    isSuccess: isSuccessDelete,
  } = useDeleteData(api);

  const { data, isLoading } = useFetchData<PageableResponse<ProductModel>>(
    api,
    {
      resource: searchParams.get('resource') || Resource.FULL,
      search: searchParams.get('search') || '',
      length: parseInt(searchParams.get('length') || '999999'),
    },
  );
  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'UoM',
      dataIndex: 'defaultUom',
      render: (_text: string, record: ProductModel) =>
        `${record?.defaultUom?.description === undefined ? '' : record.defaultUom?.description}`,
    },
    {
      title: 'Purchase UoM',
      dataIndex: 'purchaseUom',
      render: (_text: string, record: ProductModel) =>
        `${record?.purchaseUom?.description === undefined ? '' : record.purchaseUom?.description}`,
    },
    // {
    //   title: 'Category',
    //   dataIndex: 'productCategory',
    //   render: (_text: string, record: ProductModel) =>
    //     `${record?.productCategory?.description === undefined ? '' : record.productCategory?.description}`,
    // },
    {
      title: 'Supply Method',
      dataIndex: 'supplyMethod',
    },
    {
      title: 'Product Source',
      dataIndex: 'productSource',
    },
    {
      title: 'Audit Status',
      dataIndex: 'auditStatus',
    },
    {
      title: 'Purchase Cost',
      dataIndex: 'purchaseCost',
    },
    {
      title: 'Sale Price',
      dataIndex: 'salePrice',
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      render: (_text: string, record: ProductModel) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.createdBy}</span>
            {record.timeCreated != null && (
              <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Update By/Date',
      dataIndex: 'updatedBy',
      render: (_text: string, record: ProductModel) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.updatedBy}</span>
            {record.timeUpdated != null && (
              <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
  ];

  const handleDataDelete = (data: ProductModel) => {
    mutate({ id: data.id });
    setSpinning(true);
  };

  const handleDataEdit = (data: ProductModel) => {
    productStore.setData(data);
    drawerStore.onClose();
  };

  useEffect(() => {
    if (isSuccessDelete) setSpinning(false);
  }, [isSuccessDelete]);

  const handleInfoModalClose = () => {
    setInfoModalOpen(false);
  };
  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <BaseTable
        dataSource={data?.data || []}
        isLoading={isLoading || isPendingDelete}
        handleDeleteButtonClick={handleDataDelete}
        handleEditButtonClick={handleDataEdit}
        columns={columns}
        canDelete={userModuleAccess?.delete}
        canEdit={userModuleAccess?.write}
        userRole={user?.role}
        module={moduleName}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={`${moduleName} successfully deleted`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default ProductTable;
